import React, { useState } from "react";
import { Button } from "@mui/material";
import { Download } from "@mui/icons-material";
import moment from "moment";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { useLazyListBSLReportQuery } from "store/services/BranchShipmentLocationApi";

const ExportBSLReportListScreen = ({ filters, disabled = false }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportBSLReport] = useLazyListBSLReportQuery();

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const response = await exportBSLReport({
        ...filters,
        page: 1,
        limit: 100000,
      }).unwrap();
      const data = response?.data || [];

      if (!data.length) {
        toast.info("No data to export");
        return;
      }

      const headers = [
        "Branch Name",
        "Branch Code",
        "Transit Count",
        "Destination Count",
        "Source Count",
        "Total Shipments",
      ];
      const rows = data.map((row) => [
        row.branchName,
        row.branchCode,
        Number(row.transitCount || 0),
        Number(row.destinationCount || 0),
        Number(row.sourceCount || 0),
        Number(row.totalCount || 0),
      ]);
      const totalsRow = rows.length + 2;
      rows.push(["", "Totals", null, null, null, null]);

      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

      for (let row = 1; row <= totalsRow; row += 1) {
        for (const col of [2, 3, 4, 5]) {
          const cell = XLSX.utils.encode_cell({ r: row, c: col });
          if (worksheet[cell]) worksheet[cell].z = "#,##0";
        }
      }

      worksheet[`C${totalsRow}`] = {
        t: "n",
        f: `SUM(C2:C${totalsRow - 1})`,
        v: Number(response?.totals?.totalTransit || 0),
        z: "#,##0",
      };
      worksheet[`D${totalsRow}`] = {
        t: "n",
        f: `SUM(D2:D${totalsRow - 1})`,
        v: Number(response?.totals?.totalDestination || 0),
        z: "#,##0",
      };
      worksheet[`E${totalsRow}`] = {
        t: "n",
        f: `SUM(E2:E${totalsRow - 1})`,
        v: Number(response?.totals?.totalSource || 0),
        z: "#,##0",
      };
      worksheet[`F${totalsRow}`] = {
        t: "n",
        f: `SUM(F2:F${totalsRow - 1})`,
        v: Number(response?.totals?.grandTotal || 0),
        z: "#,##0",
      };

      worksheet["!cols"] = [
        { wch: 28 },
        { wch: 16 },
        { wch: 16 },
        { wch: 20 },
        { wch: 16 },
        { wch: 18 },
      ];
      worksheet["!autofilter"] = { ref: `A1:F${totalsRow - 1}` };

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "BSL Report");
      XLSX.writeFile(
        workbook,
        `branch-shipment-location-report-${moment().format("YYYY-MM-DD")}.xlsx`,
      );
      toast.success(`Exported ${data.length} records successfully`);
    } catch (error) {
      toast.error(error?.data?.message || "Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      startIcon={<Download />}
      variant="outlined"
      size="small"
      onClick={handleExport}
      disabled={disabled || isExporting}
    >
      {isExporting ? "Exporting..." : "Export"}
    </Button>
  );
};

export default ExportBSLReportListScreen;